import { useState, useEffect, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import { Circle, ChevronDown, X, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { SelectSingle } from "./SelectSingle";
import { TextArea } from "./TextArea";
import { relatorioMovimentoOficialService } from "../services/relatorioMovimentoOficial";
import { useUserGroups } from "../hooks/useUserGroups";
import moment from "moment";

interface MeuStatusAtualProps {
  sidebarMode?: boolean;
}

export function MeuStatusAtual({ sidebarMode = false }: MeuStatusAtualProps) {
  const { grupos } = useUserGroups();

  const [loading, setLoading] = useState(false);
  const [statusAtual, setStatusAtual] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tempoDecorrido, setTempoDecorrido] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Formulário de status
  const [selectedStatus, setSelectedStatus] = useState("");
  const [tipoAtividade, setTipoAtividade] = useState("");
  const [grupoSelecionado, setGrupoSelecionado] = useState("");
  const [descricaoAtividade, setDescricaoAtividade] = useState("");

  const statusOptions = [
    { value: "ativo", label: "🟢 Ativo", color: "text-green-500" },
    { value: "inativo", label: "⚫ Inativo", color: "text-gray-500" },
    { value: "em_atividade", label: "🟡 Em Atividade", color: "text-yellow-500" },
  ];

  const tipoAtividadeOptions = [
    { value: "grupo", label: "Grupo" },
    { value: "treinamento", label: "Treinamento" },
    { value: "outros", label: "Outros" },
  ];

  useEffect(() => {
    loadStatusAtual();
  }, []);

  // Atualizar contador de tempo a cada segundo
  useEffect(() => {
    if (!statusAtual?.data_hora_inicio) {
      setTempoDecorrido("");
      return;
    }

    const updateTimer = () => {
      const inicio = moment(statusAtual.data_hora_inicio);
      const agora = moment();
      const duration = moment.duration(agora.diff(inicio));

      const horas = Math.floor(duration.asHours());
      const minutos = duration.minutes();
      const segundos = duration.seconds();

      setTempoDecorrido(`${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`);
    };

    updateTimer(); // Atualizar imediatamente
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [statusAtual]);

  const loadStatusAtual = async () => {
    try {
      const data = await relatorioMovimentoOficialService.getStatusAtual();
      setStatusAtual(data);
      if (data) {
        setSelectedStatus(data.status);
        if (data.tipo_atividade) {
          setTipoAtividade(data.tipo_atividade);
        }
        if (data.id_grupo) {
          setGrupoSelecionado(data.id_grupo);
        }
        if (data.descricao_atividade) {
          setDescricaoAtividade(data.descricao_atividade);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar status atual:", error);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      enqueueSnackbar("Selecione um status", { variant: "error" });
      return;
    }

    if (selectedStatus === "em_atividade" && !tipoAtividade) {
      enqueueSnackbar("Selecione o tipo de atividade", { variant: "error" });
      return;
    }

    if (tipoAtividade === "grupo" && !grupoSelecionado) {
      enqueueSnackbar("Selecione um grupo", { variant: "error" });
      return;
    }

    if (tipoAtividade === "outros" && !descricaoAtividade) {
      enqueueSnackbar("Descreva a atividade", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      await relatorioMovimentoOficialService.updateStatus({
        status: selectedStatus,
        tipo_atividade: selectedStatus === "em_atividade" ? tipoAtividade : undefined,
        id_grupo: tipoAtividade === "grupo" ? grupoSelecionado : undefined,
        descricao_atividade: tipoAtividade === "outros" ? descricaoAtividade : undefined,
      });

      enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      loadStatusAtual();

      // Limpar campos se necessário
      if (selectedStatus === "inativo") {
        setTipoAtividade("");
        setGrupoSelecionado("");
        setDescricaoAtividade("");
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(s => s.value === status);
    return option?.color || "text-gray-500";
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case "ativo":
        return "🟢";
      case "em_atividade":
        return "🟡";
      case "inativo":
        return "⚫";
      default:
        return "⚫";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "ativo":
        return "Ativo";
      case "em_atividade":
        return "Em Atividade";
      case "inativo":
        return "Inativo";
      default:
        return status;
    }
  };

  if (sidebarMode) {
    return (
      <div className="w-full">
        {/* Botão */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-sidebar-accent text-sm transition-colors"
          style={{ color: 'oklch(var(--sidebar-foreground) / 0.7)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base flex-shrink-0">{statusAtual ? getStatusEmoji(statusAtual.status) : "⚫"}</span>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-xs opacity-60">Meu Status</span>
              <span className="text-xs font-medium truncate">
                {statusAtual ? getStatusLabel(statusAtual.status) : "Inativo"}
                {statusAtual?.tipo_atividade === "grupo" && ` • ${statusAtual.grupo?.grupo}`}
                {statusAtual?.tipo_atividade === "treinamento" && " • Treinamento"}
                {statusAtual?.tipo_atividade === "outros" && ` • ${statusAtual.descricao_atividade}`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {tempoDecorrido && (
              <span className="text-xs font-mono text-blue-500 dark:text-blue-400">{tempoDecorrido}</span>
            )}
            <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </div>
        </button>

        {/* Painel inline expandido */}
        {isOpen && (
          <div className="w-full mt-1 rounded-md border border-sidebar-border overflow-hidden" style={{ backgroundColor: 'oklch(var(--sidebar-background))' }}>
            <div className="px-3 py-2 space-y-2">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-sidebar-border rounded bg-background text-foreground"
                >
                  <option value="">Selecione...</option>
                  {statusOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                </select>
              </div>

              {selectedStatus === "em_atividade" && (
                <>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}>Tipo de Atividade</label>
                    <select
                      value={tipoAtividade}
                      onChange={(e) => { setTipoAtividade(e.target.value); setGrupoSelecionado(""); setDescricaoAtividade(""); }}
                      className="w-full px-2 py-1.5 text-xs border border-sidebar-border rounded bg-background text-foreground"
                    >
                      <option value="">Selecione...</option>
                      {tipoAtividadeOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                  {tipoAtividade === "grupo" && (
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}>Grupo</label>
                      <select
                        value={grupoSelecionado}
                        onChange={(e) => setGrupoSelecionado(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-sidebar-border rounded bg-background text-foreground"
                      >
                        <option value="">Selecione...</option>
                        {grupos.map((g) => (<option key={g.grupo.id} value={g.grupo.id}>{g.grupo.grupo}</option>))}
                      </select>
                    </div>
                  )}
                  {tipoAtividade === "outros" && (
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'oklch(var(--sidebar-foreground) / 0.6)' }}>Descrição</label>
                      <textarea
                        value={descricaoAtividade}
                        onChange={(e) => setDescricaoAtividade(e.target.value)}
                        placeholder="Descreva a atividade..."
                        rows={2}
                        className="w-full px-2 py-1.5 text-xs border border-sidebar-border rounded bg-background text-foreground resize-none"
                      />
                    </div>
                  )}
                </>
              )}

              <Button onClick={handleUpdateStatus} disabled={loading} className="w-full" size="sm">
                {loading ? "Atualizando..." : "Atualizar"}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Botão de status compacto */}
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 8, left: rect.left });
          }
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors border border-gray-200 dark:border-gray-700"
      >
        <span className="text-lg">{statusAtual ? getStatusEmoji(statusAtual.status) : "⚫"}</span>
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500 dark:text-gray-400">Meu Status</span>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {statusAtual ? getStatusLabel(statusAtual.status) : "Inativo"}
          </span>
        </div>
        {statusAtual?.tipo_atividade && (
          <span className="text-xs text-gray-600 dark:text-gray-400 max-w-[150px] truncate">
            {statusAtual.tipo_atividade === "grupo" && `• ${statusAtual.grupo?.grupo}`}
            {statusAtual.tipo_atividade === "treinamento" && "• Treinamento"}
            {statusAtual.tipo_atividade === "outros" && `• ${statusAtual.descricao_atividade}`}
          </span>
        )}
        {tempoDecorrido && (
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 rounded text-xs font-mono text-blue-700 dark:text-blue-300">
            <Clock className="h-3 w-3" />
            {tempoDecorrido}
          </div>
        )}
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Modal de alteração de status */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="fixed w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Alterar Status</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Selecione...</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {selectedStatus === "em_atividade" && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Atividade</label>
                    <select
                      value={tipoAtividade}
                      onChange={(e) => {
                        setTipoAtividade(e.target.value);
                        setGrupoSelecionado("");
                        setDescricaoAtividade("");
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Selecione...</option>
                      {tipoAtividadeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {tipoAtividade === "grupo" && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Grupo</label>
                      <select
                        value={grupoSelecionado}
                        onChange={(e) => setGrupoSelecionado(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Selecione...</option>
                        {grupos.map((g) => (
                          <option key={g.grupo.id} value={g.grupo.id}>
                            {g.grupo.grupo}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {tipoAtividade === "outros" && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                      <textarea
                        value={descricaoAtividade}
                        onChange={(e) => setDescricaoAtividade(e.target.value)}
                        placeholder="Descreva a atividade..."
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 resize-none"
                      />
                    </div>
                  )}
                </>
              )}

              <Button
                onClick={handleUpdateStatus}
                disabled={loading}
                className="w-full"
                size="sm"
              >
                {loading ? "Atualizando..." : "Atualizar"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
